// src/dashboard/layouts/ChatLayout.tsx
import { useCallback, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { SupervisionChat } from "@/shared/components/supervisor/SupervisionChat";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { useSupervisionThreads } from "@/shared/hooks/useSupervisionThreads";
import type {
  SupervisionThread,
  SupervisionMessage,
} from "@/shared/types/supervision";
import { renderWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetId } from "@/shared/dashboard/widgets/types";
import type { MainColumnEntry } from "@/verticals/types";
import { MobileRailDrawer } from "@/shared/dashboard/MobileRailDrawer";
import { prepareDesktopApp } from "@/shared/dashboard/desktopMode";

const DESKTOP_LABEL: Record<string, string> = {
  en: "Desktop",
  uk: "Робочий стіл",
  pl: "Pulpit",
  ro: "Desktop",
};

interface Props {
  hero: { widget: WidgetId };
  mainColumn: Array<MainColumnEntry>;
  sideRail: Array<{ widget: WidgetId }>;
}

/**
 * Chat-first layout. The hero is SupervisionChat (full-height).
 * mainColumn widgets render inside the chat's empty state — only visible
 * when no thread is active.
 */
export function ChatLayout({ hero, mainColumn, sideRail }: Props) {
  const { locale } = useLanguage();
  const { vertical, verticalConfig, defaultCourseId } = useVertical();
  const supervisorName = verticalConfig.supervisor?.identity.name;
  const consumerDesktop = vertical === "general" && !defaultCourseId;

  const { refetch: refetchThreads } = useSupervisionThreads();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [initialBundle, setInitialBundle] = useState<
    { thread: SupervisionThread; messages: SupervisionMessage[] } | undefined
  >(undefined);
  const [isStartingVoice, setIsStartingVoice] = useState(false);

  const handleThreadChanged = useCallback(() => {
    refetchThreads();
  }, [refetchThreads]);
  const handleThreadCreated = useCallback(
    (thread: SupervisionThread, messages: SupervisionMessage[]) => {
      setInitialBundle({ thread, messages });
      setActiveThreadId(thread.id);
      refetchThreads();
    },
    [refetchThreads],
  );

  const startVoiceCall = () => {
    if (isStartingVoice) return;
    setIsStartingVoice(true);
    sessionStorage.setItem("supervisorStartVoice", "true");
    if (consumerDesktop) {
      prepareDesktopApp({
        id: "dr-emery",
        label: "Dr. Emery",
        icon: "message-circle",
      });
    }
    setTimeout(() => (window as any).navigate("/supervisor"), 420);
  };

  // On mobile the side-rail widgets (e.g. threadsRail) live in the
  // bottom-sheet drawer.
  const drawerWidgets = sideRail;

  const emptyStateExtras = (
    <div className="flex flex-col gap-3 w-full">
      {mainColumn.map(({ widget }, i) => (
        <div key={`${widget}-${i}`}>
          {renderWidget(widget, { slot: "mainColumn" })}
        </div>
      ))}
    </div>
  );

  // Hero widget id is resolved & validated by DashboardShell, then passed in
  // as a prop so a misconfigured config can't trip the registry check here.
  const emptyStateTitle = renderWidget(hero.widget, { slot: "hero" });

  return (
    <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden relative">
      <div
        className={cn(
          "flex-1 flex min-w-0 min-h-0 relative transition-opacity duration-400 ease-out",
          isStartingVoice && "opacity-0 pointer-events-none",
        )}
      >
        <SupervisionChat
          threadId={activeThreadId}
          initialBundle={initialBundle}
          vertical={vertical}
          locale={locale as "en" | "uk" | "pl" | "ro"}
          supervisorName={supervisorName}
          billingScope={consumerDesktop ? "general-dashboard" : undefined}
          onBack={
            activeThreadId
              ? () => {
                  setActiveThreadId(null);
                  setInitialBundle(undefined);
                }
              : undefined
          }
          backLabel={
            consumerDesktop
              ? (DESKTOP_LABEL[locale] ?? DESKTOP_LABEL.en)
              : undefined
          }
          onThreadChanged={handleThreadChanged}
          onThreadCreated={handleThreadCreated}
          onStartVoiceCall={startVoiceCall}
          emptyStateTitle={emptyStateTitle}
          emptyStateExtras={emptyStateExtras}
          // heroBackground={heroBackground}
        />
      </div>

      {sideRail.length > 0 && (
        <aside
          className={cn(
            "hidden md:flex w-80 shrink-0 flex-col border-l border-[var(--hairline)] bg-[var(--paper-2)]",
            "transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)]",
            isStartingVoice ? "translate-x-full" : "translate-x-0",
          )}
        >
          {sideRail.map(({ widget }, i) => (
            <div
              key={`${widget}-${i}`}
              className={cn(
                "flex flex-col min-h-0",
                sideRail.length > 1 ? "flex-1 basis-0" : "flex-1",
                i > 0 && "border-t border-[var(--hairline)]",
              )}
            >
              {renderWidget(widget, { slot: "sideRail" })}
            </div>
          ))}
        </aside>
      )}

      {/* Mobile drawer with a peek handle. Hidden inside an active supervisor
          conversation so it doesn't cover the chat composer. On mobile the
          drawer only holds widgets that weren't pulled out above the composer
          (e.g. conversations); the clients-schedule card renders inline. */}
      {drawerWidgets.length > 0 && !activeThreadId && (
        <MobileRailDrawer widgets={drawerWidgets} peekLabel="Conversations" />
      )}
    </div>
  );
}
